// Свой набор иконок вместо эмодзи.
//
// Эмодзи рендерятся по-разному в каждой ОС, тянут за собой цвет (а тема у нас
// строго чёрно-белая) и не масштабируются под размер текста. Здесь всё на
// одной сетке 24×24, штрих 1.5, цвет — currentColor, поэтому иконка всегда
// в тон соседней надписи.
//
// Библиотеку не берём: набор маленький, а свой контур точнее ложится в макет.

interface IconProps {
  /** Размер стороны в пикселях. По умолчанию 20 — под строку интерфейса. */
  size?: number
  className?: string
}

function Icon({
  size = 20,
  className,
  children,
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.65}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

/**
 * The brand mark: the letter О as a ring struck by light from one side.
 * Same geometry as favicon.svg, minus the tile, in currentColor — the band
 * is thin where the shape is shadowed and thick where the light lands.
 */
export function Mark({ size = 20, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="currentColor"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path
        fillRule="evenodd"
        d="M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm-3 4a13 13 0 1 1 0 26 13 13 0 0 1 0-26Z"
      />
    </svg>
  )
}

export function Compose(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M12 4H6.5A2.5 2.5 0 0 0 4 6.5v11A2.5 2.5 0 0 0 6.5 20h11a2.5 2.5 0 0 0 2.5-2.5V12" />
      <path d="m18.4 2.6 3 3-8.9 8.9-3.6.6.6-3.6 8.9-8.9Z" />
    </Icon>
  )
}

export function Pin({ size = 20, className }: IconProps) {
  return (
    <Icon size={size} className={className}>
      <path d="M9 3.5h6M10.4 3.5l-.6 6.2-3.3 2.6V15h11v-2.7l-3.3-2.6-.6-6.2M12 15v5.5" />
    </Icon>
  )
}

/** Закреплённый — тот же контур, но залитый: состояние видно без подписи. */
export function PinFilled({ size = 20, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d="M10.4 3.5h3.2l-.6 6.2 3.3 2.6V15h-11v-2.7l3.3-2.6-.6-6.2Z" />
      <path d="M9 3.5h6M12 15v5.5" fill="none" />
    </svg>
  )
}

export function Pencil(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="m4 20 4-1L19 8a2.1 2.1 0 0 0-3-3L5 16l-1 4Z" />
      <path d="m14.5 6.5 3 3" />
    </Icon>
  )
}

export function Trash(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M4.5 6.5h15M10 6.5V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1.5M6.5 6.5l.8 12.1a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4l.8-12.1" />
    </Icon>
  )
}

export function ArrowUp(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M12 19.5v-15M5.5 11 12 4.5 18.5 11" />
    </Icon>
  )
}

export function Stop({ size = 20, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <rect x="7" y="7" width="10" height="10" rx="2.5" />
    </svg>
  )
}

export function Paperclip(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M19.5 11.5 12 19a5 5 0 0 1-7-7l8-8a3.5 3.5 0 0 1 5 5l-8 8a2 2 0 0 1-3-3l7.5-7.5" />
    </Icon>
  )
}

export function Sun(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2M12 19.5v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2.5 12h2M19.5 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
    </Icon>
  )
}

export function Moon(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M20 14.2A8.5 8.5 0 0 1 9.8 4 8.5 8.5 0 1 0 20 14.2Z" />
    </Icon>
  )
}

export function ChevronDown(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="m6.5 9.5 5.5 5.5 5.5-5.5" />
    </Icon>
  )
}

export function Copy(p: IconProps) {
  return (
    <Icon {...p}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4.5A1.5 1.5 0 0 1 3 13.5v-9A1.5 1.5 0 0 1 4.5 3h9A1.5 1.5 0 0 1 15 4.5V5" />
    </Icon>
  )
}

export function Check(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="m5 12.5 4.5 4.5L19 7" />
    </Icon>
  )
}

export function Close(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="m6 6 12 12M18 6 6 18" />
    </Icon>
  )
}

export function Menu(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </Icon>
  )
}

/** Метка строки диалога в списке. */
export function Bubble(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M20.5 11.8a8 8 0 0 1-8.5 8 9 9 0 0 1-3.8-.9L3.5 20.5l1.6-4.6a8 8 0 0 1-1.6-4.8 8 8 0 0 1 8.5-7.6 8 8 0 0 1 8.5 7.6Z" />
    </Icon>
  )
}

export function Exit(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M14 4h4.5A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5H14" />
      <path d="m9 8 4 4-4 4M13 12H3.5" />
    </Icon>
  )
}

export function Download(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M12 3.5v11M7.5 10 12 14.5 16.5 10M4.5 19.5h15" />
    </Icon>
  )
}

/**
 * Attach a file. Mirrors Download exactly — arrow reversed over the same
 * baseline — so sending and receiving a file read as one pair. A paperclip
 * sat here before: its diagonal closed loop was the only curve in an
 * otherwise orthogonal set, so it read as a sticker rather than an icon.
 */
export function Upload(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M12 15.5v-11M7.5 9 12 4.5 16.5 9M4.5 19.5h15" />
    </Icon>
  )
}

export function Search(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m15.5 15.5 4.5 4.5" />
    </Icon>
  )
}

/** Collapse/expand the conversation panel. */
export function PanelLeft(p: IconProps) {
  return (
    <Icon {...p}>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
      <path d="M9.5 4.5v15" />
    </Icon>
  )
}

export function Plus(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M12 5v14M5 12h14" />
    </Icon>
  )
}

export function More(p: IconProps) {
  return <Icon {...p}><circle cx="5" cy="12" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /></Icon>
}

export function Document(p: IconProps) {
  return <Icon {...p}><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z" /><path d="M14 3v6h6M8 13h8M8 17h5" /></Icon>
}

export function Spinner(p: IconProps) {
  return <Icon {...p}><path d="M21 12a9 9 0 1 1-9-9" /></Icon>
}

export function Clock(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </Icon>
  )
}

export function Brain(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M12 5.5a2.5 2.5 0 0 0-5 0 2.5 2.5 0 0 0-2 4 2.5 2.5 0 0 0 1.5 4.3 2.5 2.5 0 0 0 5 .7V5.5Z" />
      <path d="M12 5.5a2.5 2.5 0 0 1 5 0 2.5 2.5 0 0 1 2 4 2.5 2.5 0 0 1-1.5 4.3 2.5 2.5 0 0 1-5 .7V5.5Z" />
      <path d="M12 15v5.5" />
    </Icon>
  )
}

/**
 * Background tasks: a checklist, two items done.
 *
 * Replaces the 🧩 emoji that used to open the panel. An emoji brought its own
 * colour into a palette that has none — the green of the puzzle piece was the
 * only saturated thing on the screen — and rendered differently on every OS.
 */
export function Tasks(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="m4 7.5 1.6 1.6L8.8 5.9" />
      <path d="M12.5 7.5h7.5" />
      <path d="m4 16.5 1.6 1.6 3.2-3.2" />
      <path d="M12.5 16.5h7.5" />
    </Icon>
  )
}
